import React from 'react';
import { AlertTriangle, CheckCircle2, Link2Off, Target, TrendingDown } from 'lucide-react';
import type { EfficiencyMatrixResult } from '../services/lstCorrelation';

interface LstEfficiencyMatrixProps {
  result: EfficiencyMatrixResult;
}

const TONE_BORDER = {
  emerald: 'border-emerald-200 dark:border-emerald-900/50',
  amber: 'border-amber-200 dark:border-amber-900/50',
  red: 'border-red-200 dark:border-red-900/50',
  slate: 'border-slate-200 dark:border-slate-700',
};

const TONE_BG = {
  emerald: 'bg-emerald-50/80 dark:bg-emerald-900/15',
  amber: 'bg-amber-50/80 dark:bg-amber-900/15',
  red: 'bg-red-50/80 dark:bg-red-900/15',
  slate: 'bg-slate-50/80 dark:bg-slate-900/30',
};

const ICON = {
  eficaz: CheckCircle2,
  padrao_ineficaz: AlertTriangle,
  processo_instavel: TrendingDown,
  critico: AlertTriangle,
  indisponivel: Link2Off,
};

/**
 * Matriz de Eficácia — Eixo X: Aderência (%) × Eixo Y: Atingimento da Meta (%).
 */
export const LstEfficiencyMatrix: React.FC<LstEfficiencyMatrixProps> = ({ result }) => {
  const Icon = ICON[result.quadrant] ?? Target;
  const disabled = !result.resultAvailable;

  return (
    <div className={`rounded-lg border p-3 ${TONE_BORDER[result.tone]} ${TONE_BG[result.tone]}`}>
      <div className="mb-2 flex items-start gap-2">
        <Icon
          size={18}
          className={
            result.tone === 'emerald'
              ? 'text-emerald-500'
              : result.tone === 'amber'
                ? 'text-amber-500'
                : result.tone === 'red'
                  ? 'text-red-500'
                  : 'text-slate-400'
          }
        />
        <div className="min-w-0 flex-1">
          <h4 className="text-[12px] font-bold text-slate-800 dark:text-slate-100">{result.title}</h4>
          {result.indicatorName ? (
            <p className="text-[10px] text-slate-500 dark:text-slate-400">Indicador: {result.indicatorName}</p>
          ) : (
            <p className="text-[10px] font-medium text-slate-400">Sem indicador vinculado</p>
          )}
        </div>
      </div>

      {/* Eixos */}
      <div className="mb-1 flex justify-between text-[8px] font-bold uppercase tracking-wide text-slate-400">
        <span>← Baixa aderência</span>
        <span>Alta aderência →</span>
      </div>

      {/* Quadrante 2×2 — Y = atingimento da meta */}
      <div
        className={`mb-2 grid grid-cols-2 gap-1 text-center text-[9px] font-bold uppercase tracking-wide ${
          disabled ? 'opacity-45 pointer-events-none' : ''
        }`}
        aria-disabled={disabled}
      >
        <QuadrantCell
          label="Processo instável"
          sub="Baixa adh. × Alto resultado"
          active={result.quadrant === 'processo_instavel'}
          tone="amber"
        />
        <QuadrantCell
          label="Processo excelente"
          sub="Alta adh. × Alto resultado"
          active={result.quadrant === 'eficaz'}
          tone="emerald"
        />
        <QuadrantCell
          label="Situação crítica"
          sub="Baixa adh. × Baixo resultado"
          active={result.quadrant === 'critico'}
          tone="red"
        />
        <QuadrantCell
          label="Padrão ineficaz"
          sub="Alta adh. × Baixo resultado"
          active={result.quadrant === 'padrao_ineficaz'}
          tone="amber"
        />
      </div>

      <div className="mb-1 text-center text-[8px] font-bold uppercase tracking-wide text-slate-400">
        ↑ Alto atingimento da meta · Baixo atingimento ↓
      </div>

      {disabled && (
        <div className="mb-2 rounded border border-amber-200/80 bg-amber-50/60 px-2 py-1.5 text-[10px] leading-snug text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          {result.message}
        </div>
      )}

      {!disabled && (
        <p className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">{result.message}</p>
      )}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] tabular-nums text-slate-500">
        <span>
          Eixo X — Aderência:{' '}
          <strong className="text-slate-700 dark:text-slate-200">{result.adherencePct}%</strong>
        </span>
        <span>
          Eixo Y — Nota de Resultado:{' '}
          <strong
            className={
              result.resultAvailable
                ? 'text-slate-700 dark:text-slate-200'
                : 'text-slate-400 dark:text-slate-500'
            }
          >
            {result.resultLabel}
          </strong>
        </span>
      </div>

      {result.resultScore?.realized != null && result.resultScore.target != null && result.resultAvailable && (
        <p className="mt-1 text-[9px] text-slate-400">
          Realizado: {result.resultScore.realized.toFixed(2)} {result.resultScore.unit} · Meta:{' '}
          {result.resultScore.target} {result.resultScore.unit}
          {result.resultScore.detail ? ` · ${result.resultScore.detail}` : ''}
        </p>
      )}
    </div>
  );
};

const QuadrantCell: React.FC<{
  label: string;
  active: boolean;
  sub: string;
  tone?: 'emerald' | 'amber' | 'red';
}> = ({ label, active, sub, tone }) => (
  <div
    className={`rounded px-1 py-1.5 transition-all ${
      active
        ? tone === 'emerald'
          ? 'bg-emerald-500 text-white shadow-sm'
          : tone === 'amber'
            ? 'bg-amber-500 text-white shadow-sm'
            : tone === 'red'
              ? 'bg-red-500 text-white shadow-sm'
              : 'bg-violet-500 text-white shadow-sm'
        : 'bg-white/60 text-slate-400 dark:bg-slate-900/40'
    }`}
  >
    <div>{label}</div>
    <div className="text-[8px] font-medium opacity-80">{sub}</div>
  </div>
);

export default LstEfficiencyMatrix;
